import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

export default function ConsultationColle() {
  const { t, ui } = useI18n();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  // --- Contrôles tri / filtre ---
  const [sortKey, setSortKey] = useState("index");
  const [sortDir, setSortDir] = useState("asc");
  const [filterKey, setFilterKey] = useState("");
  const [filterValue, setFilterValue] = useState("");

  const [filterKey2, setFilterKey2] = useState("");
  const [filterValue2, setFilterValue2] = useState("");

  function applyFilter(arr, key, value) {
    if (!key || String(value).trim() === "") return arr;
    const needle = String(value).toLowerCase();
    return arr.filter((r) => {
      const v = r[key];
      if (v == null) return false;
      return String(v).toLowerCase().includes(needle);
    });
  }

  // --- Helpers formatage ---
  const rmAccents = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const asFrNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v))
      return v.toLocaleString("fr-FR", { useGrouping: false });
    if (typeof v === "string") {
      const n = Number(v.replace(",", "."));
      if (Number.isFinite(n))
        return n.toLocaleString("fr-FR", { useGrouping: false });
    }
    return v;
  };

  const toCsvValue = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // --- Export CSV ---
  function handleExportCsv(viewRows) {
    const headers = [
      "INDEX",
      "NATURE",
      "FOURNISSEUR",
      "DATE RECEPTION",
      "DATE PEREMPTION",
      "QUANTITE TOTALE",
      "NOMBRE LOT",
      "QUANTITE",
      "COLORI",
      "TEMPERATURE",
      "VISCOSITE",
      "SOLID CONTENT",
      "PRIX / M3 (€)",
      "NO BATCH",
      "PN",
      "STATUT",
    ].map(rmAccents);

    const data = viewRows.map((r) => [
      r.index,
      r.nature,
      r.fournisseur,
      r.dateReception,
      r.datePeremption,
      asFrNumber(r.quantiteTotale),
      asFrNumber(r.nombreLot),
      asFrNumber(r.quantite),
      r.colori,
      asFrNumber(r.temperature),
      asFrNumber(r.viscosite),
      asFrNumber(r.solidContent),
      asFrNumber(r.prixMetreCube),
      r.noBatch,
      r.pn,
      r.statut,
    ]);

    const csvBody = [headers, ...data]
      .map((row) => row.map(toCsvValue).join(";"))
      .join("\n");
    const csv = `sep=;\n${csvBody}`;
    const blob = new Blob(["\uFEFF", csv], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    const d = new Date();
    const ts = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(d.getDate()).padStart(2, "0")}`;
    a.href = URL.createObjectURL(blob);
    a.download = `colle_stock_${ts}.csv`;
    a.click();
  }

  // --- Chargement ---
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await apiFetch("/api/colle/list");
        if (!r.ok) throw new Error("Erreur serveur");
        const data = await r.json();

        const mapped = (data || []).map((it, i) => ({
          index: it.index || i + 1,
          pn: it.PN,
          nature: it.Nature,
          fournisseur: it.Fournisseur,
          dateReception: formatDate(it.DateReception),
          datePeremption: formatDate(it.DatePeremption),
          quantiteTotale: toNumber(it.QuantiteTotale),
          nombreLot: toNumber(it.NombreLot),
          quantite: toNumber(it.Quantite),
          colori: it.Colori,
          temperature: toNumber(it.Temperature),
          viscosite: toNumber(it.Viscosite),
          solidContent: toNumber(it.SolidContent),
          prixMetreCube: toNumber(it.PrixMetreCube),
          noBatch: it.NoBatch,
          statut: it.Statut,
          raw: it,
        }));

        if (!cancelled) setRows(mapped);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- listes pour filtres ---
  const fournisseurs = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.fournisseur).filter(Boolean)),
      ).sort(),
    [rows],
  );
  const etats = useMemo(
    () => Array.from(new Set(rows.map((r) => r.statut).filter(Boolean))).sort(),
    [rows],
  );

  const view = useMemo(() => {
    let out = [...rows];
    out = applyFilter(out, filterKey, filterValue);
    out = applyFilter(out, filterKey2, filterValue2);
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => compareByKey(a, b, sortKey) * dir);
    return out;
  }, [
    rows,
    filterKey,
    filterValue,
    filterKey2,
    filterValue2,
    sortKey,
    sortDir,
  ]);

  const handleEdit = (rawItem) => {
    localStorage.setItem("woodoo_edit_item", JSON.stringify(rawItem));
    navigate("/logistique/reception/colle", {
      state: { item: rawItem, edit: true },
    });
  };

  return (
    <div style={styles.page}>
      <MenuBar active="logistique" />
      <div style={styles.wrap} className="wrap">
        <section style={styles.panel} className="card">
          <div style={styles.panelTitle}>SUIVI STOCK COLLE</div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>LOTS COLLE</div>

            {/* Toolbar tri / filtre / export */}
            <div style={styles.toolbar} className="toolbar">
              <div style={styles.group} className="group">
                <span style={styles.label}>{ui("Trier par :")}</span>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  style={styles.select}
                >
                  <option value="index">Index</option>
                  <option value="pn">PN</option>
                  <option value="nature">Nature</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="dateReception">Date réception</option>
                  <option value="datePeremption">Date péremption</option>
                  <option value="quantiteTotale">Quantité totale</option>
                  <option value="nombreLot">Nombre de lot</option>
                  <option value="quantite">Quantité</option>
                  <option value="colori">Colori</option>
                  <option value="temperature">Température</option>
                  <option value="viscosite">Viscosité</option>
                  <option value="solidContent">Solid content</option>
                  <option value="prixMetreCube">Prix / m³</option>
                  <option value="noBatch">No Batch</option>
                  <option value="statut">Statut</option>
                </select>
                <button
                  onClick={() =>
                    setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                  }
                  style={styles.button}
                >
                  {sortDir === "asc" ? "↗︎ Asc" : "↘︎ Desc"}
                </button>
              </div>

              <div style={styles.group}>
                <span style={styles.label}>Filtrer :</span>
                <select
                  value={filterKey}
                  onChange={(e) => {
                    setFilterKey(e.target.value);
                    setFilterValue("");
                  }}
                  style={styles.select}
                >
                  <option value="">{ui("— colonne —")}</option>
                  <option value="pn">PN</option>
                  <option value="nature">Nature</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="colori">Colori</option>
                  <option value="noBatch">No Batch</option>
                  <option value="statut">Statut</option>
                </select>

                {filterKey === "fournisseur" ? (
                  <select
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {fournisseurs.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                ) : filterKey === "statut" ? (
                  <select
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {etats.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    placeholder="valeur…"
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    style={styles.input}
                  />
                )}
              </div>

              {/* 2e filtre (AND) */}
              <div style={styles.group}>
                <span style={styles.label}>Et :</span>
                <select
                  value={filterKey2}
                  onChange={(e) => {
                    setFilterKey2(e.target.value);
                    setFilterValue2("");
                  }}
                  style={styles.select}
                >
                  <option value="">{ui("— colonne —")}</option>
                  <option value="pn">PN</option>
                  <option value="nature">Nature</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="colori">Colori</option>
                  <option value="noBatch">No Batch</option>
                  <option value="statut">Statut</option>
                </select>

                {filterKey2 === "fournisseur" ? (
                  <select
                    value={filterValue2}
                    onChange={(e) => setFilterValue2(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {fournisseurs.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                ) : filterKey2 === "statut" ? (
                  <select
                    value={filterValue2}
                    onChange={(e) => setFilterValue2(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {etats.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    placeholder="valeur…"
                    value={filterValue2}
                    onChange={(e) => setFilterValue2(e.target.value)}
                    style={styles.input}
                  />
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  setFilterKey("");
                  setFilterValue("");
                  setFilterKey2("");
                  setFilterValue2("");
                  setSortKey("index");
                  setSortDir("asc");
                }}
                style={{
                  ...styles.button,
                  background: "#fff",
                  color: "#000",
                }}
              >
                {ui("Réinitialiser")}
              </button>

              <button
                onClick={() => handleExportCsv(view)}
                style={styles.button}
              >
                {ui("Export CSV")}
              </button>
            </div>

            {/* Tableau */}
            <div style={{ overflowX: "auto" }} className="tableWrap">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <Th>{ui("INDEX")}</Th>
                    <Th>NATURE</Th>
                    <Th>{ui("FOURNISSEUR")}</Th>
                    <Th>DATE RÉCEPTION</Th>
                    <Th>DATE PÉREMPTION</Th>
                    <Th>{ui("QUANTITÉ TOTALE")}</Th>
                    <Th>NOMBRE LOT</Th>
                    <Th>QUANTITÉ</Th>
                    <Th>COLORI</Th>
                    <Th>TEMPÉRATURE</Th>
                    <Th>VISCOSITÉ</Th>
                    <Th>{ui("SOLID CONTENT")}</Th>
                    <Th>PRIX / M³ (€)</Th>
                    <Th>NO BATCH</Th>
                    <Th>PN</Th>
                    <Th>{ui("STATUT")}</Th>
                    <Th>{ui("ACTIONS")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={17} style={styles.loadingCell}>
                        {ui("Chargement…")}
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    view.map((r, i) => (
                      <tr key={i}>
                        <Td center>{r.index}</Td>
                        <Td>{r.nature}</Td>
                        <Td>{r.fournisseur}</Td>
                        <Td center>{r.dateReception}</Td>
                        <Td center>{r.datePeremption}</Td>
                        <Td center>{r.quantiteTotale}</Td>
                        <Td center>{r.nombreLot}</Td>
                        <Td center>{r.quantite}</Td>
                        <Td center>{r.colori}</Td>
                        <Td center>{r.temperature}</Td>
                        <Td center>{r.viscosite}</Td>
                        <Td center>{r.solidContent}</Td>
                        <Td center>{r.prixMetreCube}</Td>
                        <Td center>{r.noBatch}</Td>
                        <Td>{r.pn}</Td>
                        <Td center>{r.statut}</Td>
                        <Td center>
                          <button
                            style={styles.actionBtn}
                            onClick={() => handleEdit(r.raw)}
                          >
                            MODIFIER
                          </button>
                        </Td>
                      </tr>
                    ))}

                  {!loading && view.length === 0 && (
                    <tr>
                      <td colSpan={17} style={styles.loadingCell}>
                        Aucune donnée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {err && <div style={styles.error}>⚠ {err}</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ===== Helpers ===== */
function formatDate(d) {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  }
  return d;
}
function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
}
function compareByKey(a, b, key) {
  const va = a[key],
    vb = b[key];

  const numericKeys = [
    "index",
    "quantiteTotale",
    "nombreLot",
    "quantite",
    "temperature",
    "viscosite",
    "solidContent",
    "prixMetreCube",
  ];

  if (numericKeys.includes(key)) {
    return (Number(va) || 0) - (Number(vb) || 0);
  }

  return String(va || "").localeCompare(String(vb || ""), "fr", {
    sensitivity: "base",
  });
}
function Th({ children }) {
  const { ui } = useI18n();
  const _c = typeof children === "string" ? ui(children) : children;

  return <th style={styles.th}>{_c}</th>;
}
function Td({ children, center }) {
  return (
    <td
      style={{
        ...styles.td,
        textAlign: center ? "center" : "left",
      }}
    >
      {children}
    </td>
  );
}

/* ===== Styles ===== */
const LIME = "#E6FF3E";
const styles = {
  page: {
    minHeight: "100vh",
    background: "#000",
    color: "#fff",
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  wrap: { padding: "16px" },
  panel: { background: "#63686f", border: "1px solid #3c4046", padding: 16 },
  panelTitle: {
    color: "#fff",
    fontWeight: 800,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  card: { background: "#e9eaed", border: "1px solid #c9cbd1" },
  cardTitle: {
    background: "#d8d9db",
    color: "#000",
    fontWeight: 800,
    textTransform: "uppercase",
    padding: "10px 16px",
    borderBottom: "1px solid #c9cbd1",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    borderBottom: "1px solid #c9cbd1",
    background: "#f3f4f6",
  },
  group: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  label: { color: "#000", fontWeight: 700, fontSize: 12 },
  select: {
    height: 34,
    border: "1px solid #c0c0c0",
    background: "#fff",
    padding: "0 8px",
  },
  input: {
    height: 34,
    border: "1px solid #c0c0c0",
    background: "#fff",
    padding: "0 8px",
  },
  button: {
    height: 34,
    padding: "0 10px",
    border: "none",
    background: LIME,
    color: "#000",
    fontWeight: 800,
    cursor: "pointer",
  },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff" },
  th: {
    background: "#d9d9d9",
    color: "#000",
    fontWeight: 800,
    textTransform: "uppercase",
    border: "1px solid #b7b7b7",
    padding: "10px 6px",
  },
  td: {
    background: "#fff",
    color: "#000",
    border: "1px solid #c7c7c7",
    padding: "8px 6px",
    fontWeight: 500,
  },
  actionBtn: {
    background: LIME,
    color: "#000",
    fontWeight: 800,
    border: "none",
    width: "100%",
    height: 36,
    cursor: "pointer",
  },
  error: { marginTop: 10, color: "#ffb3b3", fontWeight: 600 },
  loadingCell: { padding: 16, textAlign: "center", background: "#fff" },
};

/* ✅ Responsive media queries */
const mediaStyle = document.createElement("style");
mediaStyle.innerHTML = `
/* --- Mobile --- */
@media (max-width: 639px) {
  .wrap { padding: 8px !important; }
  .toolbar { flex-direction: column !important; align-items: stretch !important; }
  .group { flex-direction: column !important; align-items: stretch !important; width: 100% !important; }
  .tableWrap { overflow-x: auto; }
  table { font-size: 13px !important; }
  th, td { padding: 8px 4px !important; }
  .actionBtn { font-size: 13px !important; height: 40px !important; }
}

/* --- Tablette --- */
@media (min-width: 640px) and (max-width: 1023px) {
  .wrap { padding: 12px 24px !important; }
  .toolbar { flex-wrap: wrap !important; gap: 8px !important; }
}

/* --- Desktop --- */
@media (min-width: 1024px) {
  .wrap { padding: 24px 64px !important; }
  .toolbar { gap: 12px !important; }
  th, td { padding: 10px 8px !important; }
}
`;
document.head.appendChild(mediaStyle);
